import * as React from 'react';

import {
  Button,
  cn,
  ComboBox,
  ContentCard,
  ContentSection,
  LocalizedDate,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { Link } from 'lucide-react';

import * as state from '../../_state/containerDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { tradeRoutePaths as tradeRoutes } from '../../routes';
import { ContainerRecord, enumContainerStatus } from '../../types/container';

import { ContainerStatusBadge } from '~/shared/components/ContainerStatusBadge';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

interface ContainerTabOverviewProps {
  container: ContainerRecord;
  onRefetch?: () => void;
}

export const ContainerTabOverview: React.FC<ContainerTabOverviewProps> = ({
  container,
  onRefetch,
}) => {
  const t = useTranslations();
  const toast = useToast();
  const [, navigate] = useModuleLocation();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const updateContainer = useSetAtom(state.$updateContainer);

  const [editForm, setEditForm] = React.useState({
    containerNumber: container.containerNumber ?? '',
    sealNumber: container.sealNumber ?? '',
  });

  const handleCancelEdit = React.useCallback(() => {
    setEditForm({
      containerNumber: container.containerNumber ?? '',
      sealNumber: container.sealNumber ?? '',
    });
  }, [container.containerNumber, container.sealNumber]);

  const handleSave = React.useCallback(async () => {
    try {
      setIsSubmitting(true);

      await updateContainer({
        id: container.id,
        containerNumber: editForm.containerNumber || null,
        sealNumber: editForm.sealNumber || null,
      });

      toast.success(t.containersView.detail.messages.updateSuccess);
      onRefetch?.();
    } catch (err) {
      toast.error(
        t.containersView.detail.messages.updateError(
          err instanceof Error ? err.message : t.common.unknown,
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    updateContainer,
    container.id,
    editForm.containerNumber,
    editForm.sealNumber,
    toast,
    onRefetch,
    t,
  ]);

  const handleNavigateToBooking = React.useCallback(() => {
    if (container.bookingId) {
      navigate(tradeRoutes.bookingDetail, {
        params: { id: container.bookingId },
        keepSearchParams: false,
      });
    }
  }, [container.bookingId, navigate]);

  const handleNavigateToLoad = React.useCallback(() => {
    if (container.loadId) {
      navigate(tradeRoutes.loadDetail, {
        params: { id: container.loadId },
        keepSearchParams: false,
      });
    }
  }, [container.loadId, navigate]);

  const canEdit =
    container.status !== enumContainerStatus.Shipped &&
    container.status !== enumContainerStatus.Voided;

  return (
    <div className="@ContainerTabOverview flex max-h-full flex-col gap-5 p-5">
      <ContentCard noXPadding className="pb-0">
        {/* Container Information */}
        <ContentSection
          variant="panel"
          title={t.containersView.detail.sections.containerInfo}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField
              label={t.containersView.detail.fields.containerNumber}
              value={editForm.containerNumber}
              onChange={(value) =>
                setEditForm((prev) => ({
                  ...prev,
                  containerNumber: value,
                }))
              }
              placeholder={t.containersView.detail.placeholders.containerNumber}
              isDisabled={!canEdit}
            />

            <TextField
              label={t.containersView.detail.fields.sealNumber}
              value={editForm.sealNumber}
              onChange={(value) =>
                setEditForm((prev) => ({ ...prev, sealNumber: value }))
              }
              placeholder={t.containersView.detail.placeholders.sealNumber}
              isDisabled={!canEdit}
            />

            <TextField
              label={t.containersView.detail.fields.containerType}
              value={container.containerType?.name}
              isDisabled
            />

            <ComboBox
              isDisabled
              label={t.containersView.detail.fields.status}
              options={[{ label: container.status, id: container.status }]}
              value={[container.status]}
              renderSelection={() => (
                <ContainerStatusBadge status={container.status} />
              )}
            />
          </div>
        </ContentSection>

        {/* Related Entities */}
        <ContentSection
          variant="panel"
          title={t.containersView.detail.sections.relatedEntities}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ComboBox
              isDisabled
              label={t.containersView.detail.fields.booking}
              options={
                container.booking?.id
                  ? [
                      {
                        label: container.booking?.bookingNumber,
                        id: container.booking?.id,
                      },
                    ]
                  : [
                      {
                        label: t.common.notLinked,
                        id: t.common.notLinked,
                      },
                    ]
              }
              value={
                container.booking?.id
                  ? [container.booking?.id]
                  : [t.common.notLinked]
              }
              renderSelection={() =>
                container.booking ? (
                  <Button
                    variant="link"
                    onPress={handleNavigateToBooking}
                    className="flex max-h-[21px] items-center gap-1 p-0 font-medium"
                  >
                    <Link className="size-3" />
                    {container.booking?.bookingNumber}
                  </Button>
                ) : (
                  <p className="text-neutral/40">{t.common.notLinked}</p>
                )
              }
            />

            <ComboBox
              isDisabled
              label={t.containersView.detail.fields.load}
              options={
                container.load?.id
                  ? [
                      {
                        label: container.load?.documentNumber,
                        id: container.load?.id,
                      },
                    ]
                  : [
                      {
                        label: t.common.notLinked,
                        id: t.common.notLinked,
                      },
                    ]
              }
              value={
                container.load?.id ? [container.load?.id] : [t.common.notLinked]
              }
              renderSelection={() =>
                container.load ? (
                  <Button
                    variant="link"
                    onPress={handleNavigateToLoad}
                    className="flex max-h-[21px] items-center gap-1 p-0 font-medium"
                  >
                    <Link className="size-3" />#{container.load.documentNumber}
                  </Button>
                ) : (
                  <p className="text-neutral/40">{t.common.notLinked}</p>
                )
              }
            />

            <ComboBox
              isDisabled
              label={t.containersView.detail.fields.facility}
              options={
                container.facility?.id
                  ? [
                      {
                        label: container.facility?.facilityName,
                        id: container.facility?.id,
                      },
                    ]
                  : [
                      {
                        label: t.common.notLinked,
                        id: t.common.notLinked,
                      },
                    ]
              }
              value={
                container.facility?.id
                  ? [container.facility?.id]
                  : [t.common.notLinked]
              }
              renderSelection={() =>
                container.facility?.id ? (
                  <span>{container.facility?.facilityName}</span>
                ) : (
                  <span className="text-neutral/40">
                    {t.common.notAssigned}
                  </span>
                )
              }
            />
          </div>
        </ContentSection>

        {/* Timestamps */}
        <ContentSection
          variant="panel"
          title={t.containersView.detail.sections.timestamps}
          hideDivider
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs text-neutral/50">
                {t.containersView.detail.fields.createdAt}
              </label>
              <div className="font-medium">
                <LocalizedDate
                  date={container.timestamps.createdAt}
                  dateStyle="medium"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-neutral/50">
                {t.containersView.detail.fields.updatedAt}
              </label>
              <div className="font-medium">
                <LocalizedDate
                  date={container.timestamps.updatedAt}
                  dateStyle="medium"
                />
              </div>
            </div>
          </div>
        </ContentSection>

        {canEdit && (
          <div
            className={cn(
              '@ContainerTabOverviewFooterActions',
              'flex justify-end gap-3',
              'sticky bottom-0 border-t border-neutral/10 bg-white px-5 py-4',
            )}
          >
            <Button
              color="neutral"
              variant="outline"
              onPress={handleCancelEdit}
              isDisabled={isSubmitting}
            >
              {t.common.discard}
            </Button>
            <Button
              color="primary"
              variant="solid"
              onPress={handleSave}
              isPending={isSubmitting}
            >
              {t.common.save}
            </Button>
          </div>
        )}
      </ContentCard>
    </div>
  );
};
